/**
 * Locale-aware formatters — implements `docs/04-design-system.md` §5.3 exactly.
 *
 * No date library. Everything here is plain arithmetic and string building so the
 * output is byte-identical on every device, ICU build, and Hermes version. Anything
 * that depends on `Intl` would drift between Android, iOS, and CI.
 */

import { DEFAULT_LOCALE, type Locale } from './locale.ts'

// ---------------------------------------------------------------------------
// Numeral system — the one line to flip
// ---------------------------------------------------------------------------

export type NumeralSystem = 'western' | 'eastern'

/**
 * `docs/04-design-system.md` §2.3: Western Arabic numerals (0–9) are used in **both**
 * languages, including Arabic mode, because that is how Mauritanian students read
 * prices, durations, and dates in practice.
 *
 * That decision is flagged in the design system as **pending client confirmation**.
 * If the client asks for Eastern Arabic numerals (٠–٩), change this single constant
 * to `'eastern'`: every formatter below routes its digits through `toNumerals`, and
 * the unit tests cover both settings.
 */
export const NUMERAL_SYSTEM: NumeralSystem = 'western'

const WESTERN_DIGITS = '0123456789'
const EASTERN_DIGITS = '٠١٢٣٤٥٦٧٨٩'

/**
 * Rewrite every digit in `str` into the requested numeral system.
 * Accepts input already in either system, so it is safe to apply more than once.
 */
export function toNumerals(str: string, system: NumeralSystem = NUMERAL_SYSTEM): string {
  const target = system === 'eastern' ? EASTERN_DIGITS : WESTERN_DIGITS
  let out = ''
  for (const char of str) {
    const western = WESTERN_DIGITS.indexOf(char)
    if (western >= 0) {
      out += target[western]
      continue
    }
    const eastern = EASTERN_DIGITS.indexOf(char)
    out += eastern >= 0 ? target[eastern] : char
  }
  return out
}

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

/** U+2009 THIN SPACE — the thousands separator in both locales (§5.3). */
export const THIN_SPACE = '\u2009'

const CURRENCY: Record<Locale, string> = { ar: 'أوقية', fr: 'MRU' }

const UNITS: Record<Locale, { hour: string; minute: string }> = {
  ar: { hour: 'س', minute: 'د' },
  fr: { hour: 'h', minute: 'min' },
}

const DECIMAL_SEPARATOR: Record<Locale, string> = { ar: '.', fr: ',' }

const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n))

const clampSeconds = (seconds: number): number =>
  Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0

// ---------------------------------------------------------------------------
// Duration and watch time
// ---------------------------------------------------------------------------

/**
 * Playback duration and elapsed time: `12:34` under an hour, `1:02:45` over (§5.3).
 * Always reads left-to-right, in both languages — the player never mirrors (BD-6).
 */
export function formatDuration(seconds: number, system: NumeralSystem = NUMERAL_SYSTEM): string {
  const total = clampSeconds(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60
  const base = hours > 0 ? `${hours}:${pad2(minutes)}:${pad2(secs)}` : `${minutes}:${pad2(secs)}`
  return toNumerals(base, system)
}

/**
 * Watch-time totals in the progress tracker: `4 h 20 min` / `4 س 20 د` (§5.3).
 * Minutes are dropped when the total is a whole number of hours; hours are dropped
 * under one hour; zero renders as `0 min` / `0 د` rather than an empty string.
 */
export function formatWatchTime(
  seconds: number,
  locale: Locale = DEFAULT_LOCALE,
  system: NumeralSystem = NUMERAL_SYSTEM,
): string {
  const total = clampSeconds(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const unit = UNITS[locale]

  const parts: string[] = []
  if (hours > 0) parts.push(`${hours} ${unit.hour}`)
  if (minutes > 0 || hours === 0) parts.push(`${minutes} ${unit.minute}`)

  return toNumerals(parts.join(' '), system)
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/**
 * Price in ouguiya: `1 500 MRU` / `١ ٥٠٠ أوقية` (§5.3).
 *
 * Thin space (U+2009) as the thousands separator, currency at the **end edge** — which
 * renders on the left in Arabic and on the right in French purely from the paragraph
 * direction, so the string is built the same way in both locales.
 *
 * MRU has no minor unit in practice for these amounts; fractions are rounded away.
 */
export function formatPrice(
  amountMRU: number,
  locale: Locale = DEFAULT_LOCALE,
  system: NumeralSystem = NUMERAL_SYSTEM,
): string {
  const amount = Number.isFinite(amountMRU) ? Math.round(amountMRU) : 0
  return `${formatInteger(amount, system)} ${CURRENCY[locale]}`
}

/** Group an integer with the thin-space separator, without a currency. */
export function formatInteger(value: number, system: NumeralSystem = NUMERAL_SYSTEM): string {
  const rounded = Number.isFinite(value) ? Math.round(value) : 0
  const sign = rounded < 0 ? '-' : ''
  const digits = Math.abs(rounded).toString()
  let grouped = ''
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) grouped += THIN_SPACE
    grouped += digits[i]
  }
  return toNumerals(sign + grouped, system)
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** `12/03/2026` in both locales (§5.3) — day/month/year, zero-padded. */
export function formatDate(
  date: Date | number | string,
  _locale: Locale = DEFAULT_LOCALE,
  system: NumeralSystem = NUMERAL_SYSTEM,
): string {
  const d = toDate(date)
  const base = `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`
  // Returned bare. A date embedded in Arabic prose must be wrapped by the caller with
  // `ltrIsolate()` (BD-8); wrapping here would leak control characters into snapshots,
  // accessibility labels, and CSV exports.
  return toNumerals(base, system)
}

/** `12/03/2026 14:05` — used by the transaction detail screen. */
export function formatDateTime(
  date: Date | number | string,
  locale: Locale = DEFAULT_LOCALE,
  system: NumeralSystem = NUMERAL_SYSTEM,
): string {
  const d = toDate(date)
  const time = toNumerals(`${pad2(d.getHours())}:${pad2(d.getMinutes())}`, system)
  return `${formatDate(d, locale, system)} ${time}`
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
/** Relative time is used below this threshold; beyond it the absolute date is shown (§5.3). */
export const RELATIVE_TIME_CUTOFF_MS = 7 * DAY

interface RelativeStrings {
  now: string
  minutes: (n: number) => string
  hours: (n: number) => string
  days: (n: number) => string
}

/**
 * Arabic uses the dual and the 3–10 plural, so the forms are spelled out rather than
 * templated. These live here (not in the catalogues) because they are produced by the
 * formatter, which has no access to an i18next instance.
 */
const RELATIVE: Record<Locale, RelativeStrings> = {
  ar: {
    now: 'الآن',
    minutes: n =>
      n === 1
        ? 'قبل دقيقة'
        : n === 2
          ? 'قبل دقيقتين'
          : n <= 10
            ? `قبل ${n} دقائق`
            : `قبل ${n} دقيقة`,
    hours: n =>
      n === 1 ? 'قبل ساعة' : n === 2 ? 'قبل ساعتين' : n <= 10 ? `قبل ${n} ساعات` : `قبل ${n} ساعة`,
    days: n => (n === 1 ? 'قبل يوم' : n === 2 ? 'قبل يومين' : `قبل ${n} أيام`),
  },
  fr: {
    now: 'à l’instant',
    minutes: n => (n === 1 ? 'il y a 1 minute' : `il y a ${n} minutes`),
    hours: n => (n === 1 ? 'il y a 1 heure' : `il y a ${n} heures`),
    days: n => (n === 1 ? 'il y a 1 jour' : `il y a ${n} jours`),
  },
}

/**
 * Community timestamps and notification times: relative under 7 days, the absolute
 * date beyond that (§5.3). A future date falls back to the absolute date rather than
 * inventing a "in 3 days" form the app never needs.
 */
export function formatRelativeTime(
  date: Date | number | string,
  locale: Locale = DEFAULT_LOCALE,
  now: Date | number = Date.now(),
  system: NumeralSystem = NUMERAL_SYSTEM,
): string {
  const d = toDate(date)
  const reference = toDate(now)
  const delta = reference.getTime() - d.getTime()

  if (delta < 0 || delta >= RELATIVE_TIME_CUTOFF_MS) return formatDate(d, locale, system)

  const strings = RELATIVE[locale]
  if (delta < MINUTE) return strings.now
  if (delta < HOUR) return toNumerals(strings.minutes(Math.floor(delta / MINUTE)), system)
  if (delta < DAY) return toNumerals(strings.hours(Math.floor(delta / HOUR)), system)
  return toNumerals(strings.days(Math.floor(delta / DAY)), system)
}

function toDate(value: Date | number | string): Date {
  return value instanceof Date ? value : new Date(value)
}

// ---------------------------------------------------------------------------
// Sizes and percentages
// ---------------------------------------------------------------------------

/**
 * File sizes: `245 MB`, with one decimal below 10 (§5.3) — so
 * `9.4 MB` but `245 MB`. Binary units (1 KB = 1024 B), matching what the OS storage
 * settings report on the same file.
 */
export function formatFileSize(
  bytes: number,
  locale: Locale = DEFAULT_LOCALE,
  system: NumeralSystem = NUMERAL_SYSTEM,
): string {
  const safe = Number.isFinite(bytes) && bytes > 0 ? bytes : 0
  let value = safe
  let unitIndex = 0
  while (value >= 1024 && unitIndex < FILE_SIZE_UNITS.length - 1) {
    value /= 1024
    unitIndex += 1
  }

  // Bytes are never fractional; above 10 of any unit a decimal is noise.
  const text =
    unitIndex === 0 || value >= 10
      ? String(Math.round(value))
      : value.toFixed(1).replace('.', DECIMAL_SEPARATOR[locale])

  return `${toNumerals(text, system)} ${FILE_SIZE_UNITS[unitIndex]}`
}

/**
 * Completion percentages on progress rings and bars.
 * `value` is on a 0–100 scale, clamped, and rounded to whole percent by default.
 */
export function formatPercent(
  value: number,
  locale: Locale = DEFAULT_LOCALE,
  fractionDigits = 0,
  system: NumeralSystem = NUMERAL_SYSTEM,
): string {
  const safe = Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0
  const text =
    fractionDigits > 0
      ? safe.toFixed(fractionDigits).replace('.', DECIMAL_SEPARATOR[locale])
      : String(Math.round(safe))
  return `${toNumerals(text, system)}%`
}
